/**
 * Drawer — shared 560 px right slide-in shell.
 *
 * Slots: title (h2), eyebrow (mono caps), subtitle (optional mono row
 * under the title), tabs (typically a TabBar), body, footer. Backdrop
 * + slide animation + Esc-close + click-outside are owned here so
 * every drawer surface (Match details, Edit Person, Edit Team, Edit
 * User, Edit FTP target …) reads the same.
 *
 * Props:
 *   open:        boolean — render or null
 *   onClose:     () => void  (Esc, backdrop click, close button)
 *   eyebrow:     string | ReactNode — `§ MATCH · EREDIVISIE` etc.
 *   title:       ReactNode
 *   subtitle:    ReactNode (optional)
 *   tabs:        ReactNode (optional — a `<TabBar>` typically)
 *   body:        ReactNode
 *   footer:      ReactNode (optional)
 *   ariaLabel:   accessible label
 */

import { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Mono } from './index';
import styles from './Drawer.module.css';

export default function Drawer({
  open,
  onClose,
  eyebrow,
  title,
  subtitle,
  tabs,
  body,
  footer,
  ariaLabel = 'Drawer',
}) {
  const drawerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (drawerRef.current?.contains(e.target)) return;
      // Allow nested confirm/alert dialogs to bubble close clicks of
      // their own without triggering the drawer's outside-click logic.
      if (e.target.closest?.('[role="alertdialog"]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className={styles.backdrop} aria-hidden="true" />
      <aside
        className={styles.drawer}
        ref={drawerRef}
        role="dialog"
        aria-label={ariaLabel}
      >
        <div className={styles.header}>
          <div className={styles.headerBar} aria-hidden="true" />
          <div className={styles.headerText}>
            {eyebrow && (
              typeof eyebrow === 'string' ? (
                <Mono className={styles.eyebrow}>{eyebrow}</Mono>
              ) : (
                eyebrow
              )
            )}
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {tabs && <div className={styles.tabsWrapper}>{tabs}</div>}

        <div className={styles.body}>{body}</div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </aside>
    </>
  );
}
